import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowL, WhatsAppGlyph } from './Icons.jsx';
import { WA_URL } from '../config.js';
import { getAttribution } from '../attribution.js';
import { trackLead } from '../analytics.js';

const LEAD_SERVICE_OPTIONS = [
  { slug: 'web', label: 'בניית אתרים' },
  { slug: 'landing-pages', label: 'דפי נחיתה' },
  { slug: 'systems', label: 'מערכות ואינטגרציות' },
  { slug: 'automations', label: 'אוטומציות' },
  { slug: 'other', label: 'עוד לא בטוחים / משהו אחר' },
];

function newEventId() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch { /* fall through */ }
  // Older Safari / non-secure contexts: uniqueness only needs to hold long
  // enough for Meta's dedup window, so time + randomness is sufficient.
  return `lf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function LeadForm({ compact = false, defaultService = null }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(defaultService ? [defaultService] : []);
  const [status, setStatus] = useState('idle');
  const wrapRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function toggle(slug) {
    setPicked(p => p.includes(slug) ? p.filter(s => s !== slug) : [...p, slug]);
  }

  function pickedLabel() {
    if (picked.length === 0) return 'בחירת שירותים';
    if (picked.length === 1) return LEAD_SERVICE_OPTIONS.find(o => o.slug === picked[0])?.label;
    return `${picked.length} שירותים נבחרו`;
  }

  function buildMessage(values) {
    const services = picked
      .map(s => LEAD_SERVICE_OPTIONS.find(o => o.slug === s)?.label)
      .filter(Boolean)
      .join(', ');
    const lines = [
      `שלום, שמי ${values.name}.`,
      values.phone ? `טלפון: ${values.phone}` : null,
      services ? `בנושא: ${services}` : null,
      '',
      values.project,
    ].filter(Boolean);
    return lines.join('\n');
  }

  function readValues(f) {
    return {
      name: f.lf_name.value.trim(),
      phone: f.lf_phone.value.trim(),
      email: f.lf_email.value.trim(),
      project: f.lf_project.value.trim(),
      consent: f.lf_consent.checked,
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    const f = e.target;
    const v = readValues(f);
    if (!v.consent) {
      setStatus('noconsent');
      setTimeout(() => setStatus('idle'), 3500);
      return;
    }
    const services = picked
      .map(s => LEAD_SERVICE_OPTIONS.find(o => o.slug === s)?.label)
      .filter(Boolean)
      .join(', ');

    setStatus('sending');
    // One id shared by the browser pixel and the server-side Conversions API
    // call, so Meta counts this conversion exactly once. Generated here (not
    // server-side) so the browser event can fire without waiting on a
    // round-trip and still match.
    const eventId = newEventId();
    try {
      const payload = {
        name: v.name,
        phone: v.phone,
        email: v.email,
        services,
        project: v.project,
        message: buildMessage(v),
        source: window.location.href,
        ts: new Date().toISOString(),
        consent: v.consent,
        hp_website: f.lf_hp_website?.value || '',
        event_id: eventId,
        // utm_* / gclid / fbclid / referrer captured on the landing page.
        // null on a direct visit — the CRM then records the lead as `direct`
        // rather than guessing a source.
        attribution: getAttribution(),
      };
      const r = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('lead-failed');
      // Fire the real conversion event only after the backend accepted it.
      trackLead({ eventId, service: picked[0] ?? null });
      setStatus('sent');
      setTimeout(() => {
        setStatus('idle');
        f.reset();
        setPicked(defaultService ? [defaultService] : []);
      }, 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  }

  return (
    <form ref={formRef} className={'lead-form' + (compact ? ' is-compact' : '')} onSubmit={onSubmit}>
      <div className="lf-field" ref={wrapRef}>
        <label>על מה לדבר?</label>
        <div className={'lf-multi' + (open ? ' is-open' : '')}>
          <button
            type="button"
            className={'lf-multi-trigger' + (picked.length ? ' has-value' : '')}
            onClick={() => setOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span>{pickedLabel()}</span>
            <ChevronDown size={14} />
          </button>
          {open && (
            <div className="lf-multi-menu" role="listbox">
              {LEAD_SERVICE_OPTIONS.map(o => {
                const on = picked.includes(o.slug);
                return (
                  <button
                    key={o.slug}
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={'lf-multi-item' + (on ? ' is-on' : '')}
                    onClick={() => toggle(o.slug)}
                  >
                    <span className="lf-check" aria-hidden="true">{on ? '✓' : ''}</span>
                    <span>{o.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          {picked.length > 0 && (
            <div className="lf-chips">
              {picked.map(s => {
                const opt = LEAD_SERVICE_OPTIONS.find(o => o.slug === s);
                if (!opt) return null;
                return (
                  <span key={s} className="lf-chip">
                    {opt.label}
                    <button type="button" aria-label="הסר" onClick={() => toggle(s)}>x</button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="lf-row">
        <div className="lf-field">
          <label>שם <span className="lf-req" aria-hidden="true">*</span></label>
          <input name="lf_name" type="text" placeholder="איך קוראים לך" required />
        </div>
        <div className="lf-field">
          <label>טלפון / WhatsApp <span className="lf-req" aria-hidden="true">*</span></label>
          <input name="lf_phone" type="tel" placeholder="052-..." required />
        </div>
      </div>

      <div className="lf-field">
        <label>אימייל <span className="lf-req" aria-hidden="true">*</span></label>
        <input name="lf_email" type="email" placeholder="name@example.com" required />
      </div>

      <div className="lf-field">
        <label>3 משפטים על מה תקוע <span className="lf-opt">(לא חובה)</span></label>
        <textarea
          name="lf_project"
          rows={compact ? 3 : 4}
          placeholder="מה העסק. מה תקוע. מה הצעד הבא."
        ></textarea>
      </div>

      <input
        type="text"
        name="lf_hp_website"
        tabIndex={-1}
        autoComplete="new-password"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <label className="lf-consent">
        <input name="lf_consent" type="checkbox" required />
        <span>
          מאשר/ת לפוקוס מוד לשמור את הפרטים לצורך יצירת קשר חוזר, בהתאם ל
          <Link to="/privacy" target="_blank" rel="noopener">מדיניות הפרטיות</Link>.
        </span>
      </label>

      <div className="lf-actions">
        <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
          {status === 'sending' && 'שולח...'}
          {status === 'sent' && 'נשלח + נחזור אליך'}
          {status === 'error' && 'תקלה. נסו שוב'}
          {status === 'noconsent' && 'צריך לאשר את ההסכמה'}
          {status === 'idle' && (<>שליחה <ArrowL /></>)}
        </button>
      </div>
      <p className="lf-hint">
        {status === 'sent'
          ? 'ההודעה נשלחה. נחזור אליך תוך 48 שעות.'
          : 'נחזור אליך תוך 48 שעות. גם אם השארת רק שם, טלפון ומייל.'}
      </p>
    </form>
  );
}

export function LeadFormBand({ title, body, defaultService = null, eyebrow = 'השאר/י פרטים · חזרה תוך 48 שעות' }) {
  return (
    <section className="lead-band">
      <div className="wrap">
        <div className="lead-band-grid">
          <div className="reveal">
            <div className="sec-eyebrow">{eyebrow}</div>
            <h2 className="lead-band-title">
              {title || <>3 שדות. <span className="hl-block tilt-l">תשובה אמיתית</span>.</>}
            </h2>
            <p className="lead-band-body">
              {body || 'לא בטוחים שזה מתאים? כתבו בכל זאת. אם זה לא מתאים, נגיד מי כן.'}
            </p>
            <div className="lead-band-aux">
              <a className="lead-band-aux-item" href={WA_URL} target="_blank" rel="noopener noreferrer">
                <WhatsAppGlyph size={16} /> ישירות לוואטסאפ
              </a>
              <a className="lead-band-aux-item" href="mailto:yoav@focusmode.info">
                yoav@focusmode.info
              </a>
            </div>
          </div>
          <div className="reveal">
            <LeadForm compact defaultService={defaultService} />
          </div>
        </div>
      </div>
    </section>
  );
}
